import Enumerable from "linq";
import { Point } from "./Point";
import { Vector } from "./Vector";
import { Size } from "./Size";
import { IRectangle } from "./IRectangle";

export class Rectangle {    // Vectorは派生しない.(x,yは使えるが別概念なので.)
    constructor(public topLeft: Point = new Point(), public size: Size = new Size()) { }

    get x(): number {
        return this.topLeft.x;
    }

    get y(): number {
        return this.topLeft.y;
    }

    get width(): number {
        return this.size.width;
    }

    get height(): number {
        return this.size.height;
    }

    get topRight(): Point {
        return new Point(this.x + this.width, this.y);
    }

    get bottomLeft(): Point {
        return new Point(this.x, this.y + this.height);
    }

    get bottomRight(): Point {
        return this.topLeft.add(this.size.toPoint());
    }

    get center(): Point {
        return this.topLeft.add(this.size.toPoint().multiply(0.5));
    }

    move(difference: Point): Rectangle {
        return new Rectangle(this.topLeft.add(difference), this.size);
    }

    moveTo(topLeft: Point): Rectangle {
        return new Rectangle(topLeft, this.size);
    }

    contains(target: Point | Rectangle): boolean {
        if (target instanceof Point) {
            return this.contains(new Rectangle(target, new Size(0, 0)));
        }
        return (this.x <= target.x)
            && (this.y <= target.y)
            && (target.bottomRight.x <= this.bottomRight.x)
            && (target.bottomRight.y <= this.bottomRight.y);
    }

    overlaps(target: Rectangle): boolean {
        return (Math.abs(this.center.x - target.center.x) < ((this.width + target.width) / 2))
            && (Math.abs(this.center.y - target.center.y) < ((this.height + target.height) / 2));
    }

    overlapsHorizontal(target: Rectangle): boolean {
        return (Math.abs(this.center.x - target.center.x) < ((this.width + target.width) / 2));
    }

    overlapsVertical(target: Rectangle): boolean {
      return (Math.abs(this.center.y - target.center.y) < ((this.height + target.height) / 2));
    }

    add(difference: Rectangle): Rectangle {
        return new Rectangle(
            this.topLeft.add(difference.topLeft),
            this.size.add(difference.size)
        );
    }

    subtract(difference: Rectangle): Rectangle {
        return new Rectangle(
            this.topLeft.subtract(difference.topLeft),
            this.size.subtract(difference.size)
        );
    }

    multiply(multiplier: number): Rectangle {
        return new Rectangle(
            this.topLeft.multiply(multiplier),
            this.size.multiply(multiplier)
        );
    }

    reverse(): Rectangle {
        return new Rectangle(
            this.topLeft.reverse(),
            this.size.reverse()
        );
    }

    static from(source: IRectangle | Vector | Point[]): Rectangle {
        if (!source) {
            return null;
        }
        return (source instanceof Vector)
            ? this.fromPoints([source.from, source. to])
            : (source instanceof Array)
                ? this.fromPoints(source)
                : new Rectangle(new Point(source.x, source.y), new Size(source.width, source.height));
    }

    private static fromPoints(points: Point[]) {
        const xs = Enumerable.from(points)
            .select(p => p.x)
            .toArray();
        const ys = Enumerable.from(points)
            .select(p => p.y)
            .toArray();
        const topLeft = new Point(
            Math.min.apply(null, xs),
            Math.min.apply(null, ys),
        );
        const bottomRight = new Point(
            Math.max.apply(null, xs),
            Math.max.apply(null, ys),
        );
        return new Rectangle(
            topLeft,
            Size.from(topLeft.subtract(bottomRight))
        );
    }

    static to(source: Rectangle): IRectangle {
        if (!source) {
            return null;
        }
        return {
            x: source.x,
            y: source.y,
            width: source.width,
            height: source.height,
        };
    }

    clone(): Rectangle {
        return new Rectangle(
          this.topLeft.clone(),
          this.size.clone()
        );
    }
}
